import {
  CURRENCY,
  ENQUIRY_INCO_TERMS,
  TAX_OPTIONS,
  TAX_TYPE,
  WEIGHT_SYSTEM,
  countries
} from '@helpers/constants';
import {
  ICurrency,
  ICustomerEnquiryPrefill,
  ICustomerEnquiryQuote,
  INonConsolidateQuote,
  IQuotePrefill,
  IWeightChildOption,
  IWeightSystemOption
} from '@helpers/types/quotes';

/**
 * Material Location Country is stored by the backend as a country code
 * (`material_location_country`), with the name returned in
 * `material_location_country_display_value`.
 */
const getCountryOptionByCode = (code: string) => {
  const country = countries.find((item) => item.value === code);
  return country ? { label: country.label, value: country.value } : null;
};

export const DEFAULT_MATERIAL_LOCATION_COUNTRY_INTERNATIONAL = getCountryOptionByCode('IN');
export const DEFAULT_MATERIAL_LOCATION_COUNTRY_DOMESTIC = getCountryOptionByCode('US');

export const getMaterialLocationCountryOption = (
  code?: string | null,
  displayValue?: string | null
) => {
  if (!code) return null;
  return { value: code, label: displayValue || getCountryOptionByCode(code)?.label || code };
};

export const getMaterialLocationCountryPayload = (materialLocationCountry: any): string | null =>
  materialLocationCountry?.value ?? null;

export const DEFAULT_WEIGHT_SYSTEM = WEIGHT_SYSTEM.find((item) => item.value === 'kg/mt/lb') ?? {
  label: 'Kg/Mt/Lb',
  value: 'kg/mt/lb',
  childOptions: [
    {
      label: 'KG',
      value: 'KG',
      default: false,
      convertToKg: 1,
      convertToMT: 0.001,
      convertToLbs: 2.20462
    },
    {
      label: 'MT',
      value: 'MT',
      default: true,
      convertToKg: 1000,
      convertToMT: 1,
      convertToLbs: 2204.62
    },
    {
      label: 'LBS',
      value: 'LBS',
      default: false,
      convertToKg: 0.453592,
      convertToMT: 0.000453592,
      convertToLbs: 1
    }
  ]
};

export const DEFAULT_TAX_TYPE =
  TAX_OPTIONS.find((item) => item.value === TAX_TYPE.SALES_TAX) || null;

export const DEFAULT_PRIMARY_UNIT: IWeightChildOption =
  DEFAULT_WEIGHT_SYSTEM.childOptions.find((item) => item.default === true) ??
  DEFAULT_WEIGHT_SYSTEM.childOptions[0];

export const DEFAULT_CURRENCY = CURRENCY.find((item) => item.value === 'USD') || {
  value: 'USD',
  label: 'USD',
  code: 'en-US'
};

export const DEFAULT_CURRENCY2 = CURRENCY.find((item) => item.value === 'INR') || {
  value: 'INR',
  label: 'INR',
  code: 'en-IN'
};

export const DEFAULT_PRICE = {
  value: null,
  currency: DEFAULT_CURRENCY,
  cost_id: null,
  manualOverride: false
};

export const DEFAULT_CUSTOM_ISF_FILLING_CHARGES = {
  value: 385,
  currency: DEFAULT_CURRENCY,
  cost_id: null,
  manualOverride: false
};

export const DEFAULT_RATE_PRICE = {
  value: null,
  currency: DEFAULT_CURRENCY,
  cost_id: null,
  manualOverride: false,
  weightUnit: DEFAULT_PRIMARY_UNIT
};

export const DEFAULT_INCO_TERMS = {
  label: 'CIF',
  value: 'CIF'
};

export const DEFAULT_CUSTOM_CLEARANCE_PERCENTAGE = 16;

export const GENERIC_INFORMATION_DEFAULT_VALUE = {
  currency1: DEFAULT_CURRENCY,
  currency2: DEFAULT_CURRENCY2,
  fxRate: 86,
  weightSystem: DEFAULT_WEIGHT_SYSTEM,
  primaryUnit: DEFAULT_PRIMARY_UNIT,
  draftDealName: null
};

export const GENERIC_INFORMATION_DOMESTIC_DEFAULT_VALUE = {
  currency1: DEFAULT_CURRENCY,
  weightSystem: DEFAULT_WEIGHT_SYSTEM,
  primaryUnit: DEFAULT_PRIMARY_UNIT,
  draftDealName: '',
  taxType: DEFAULT_TAX_TYPE
};

export const DEFAULT_INSURANCE_COST = 0.6;

export const NON_CONSOLIDATION_ENQUIRY_QUOTE_DEFAULT_VALUE = {
  quote_id: null,
  isExpanded: true,
  productName: null,
  productPackaging: null,
  dealQuantity: null,
  dealQuantityUnit: DEFAULT_PRIMARY_UNIT,
  moq: null,
  moqUnit: DEFAULT_PRIMARY_UNIT,
  containerSize: null,
  totalWeight: 0,
  totalWeightUnit: DEFAULT_PRIMARY_UNIT,
  base_price: DEFAULT_RATE_PRICE,
  fob_price: DEFAULT_RATE_PRICE,
  cif_price: DEFAULT_RATE_PRICE,
  cfr_price: DEFAULT_RATE_PRICE,
  ddp_price: DEFAULT_RATE_PRICE,
  final_exWorks_price: DEFAULT_RATE_PRICE,
  finalQuote_selling_price: DEFAULT_RATE_PRICE,
  totalLocalFreight: DEFAULT_PRICE,
  totalPortAndClearanceExpenses: DEFAULT_PRICE,
  totalFreight: DEFAULT_PRICE,
  portOfLoading: null,
  portOfDischarge: null,
  countryOfOrigin: null,
  materialLocationCountry: DEFAULT_MATERIAL_LOCATION_COUNTRY_INTERNATIONAL,
  insuranceCost: DEFAULT_INSURANCE_COST,
  discount: null,
  profitMarginExpected: null,
  additionalCost: DEFAULT_PRICE,
  switchBlCost: DEFAULT_PRICE,
  tariff: null,
  financeCost: null,
  incoTerms: DEFAULT_INCO_TERMS,
  incoTermsLocation: null,
  incoTermsZipCode: null,
  drayageCharges: DEFAULT_PRICE,
  isfFiling: DEFAULT_CUSTOM_ISF_FILLING_CHARGES,
  customClearance: DEFAULT_CUSTOM_CLEARANCE_PERCENTAGE,
  warehousingCost: DEFAULT_PRICE
};

export const CONSOLIDATION_PRODUCT_DEFAULT_VALUE = {
  isExpanded: false,
  productName: null,
  base_price: DEFAULT_RATE_PRICE,
  fob_price: DEFAULT_RATE_PRICE,
  cif_price: DEFAULT_RATE_PRICE,
  cfr_price: DEFAULT_RATE_PRICE,
  final_exWorks_price: DEFAULT_RATE_PRICE,
  finalQuote_selling_price: DEFAULT_RATE_PRICE,
  additionalCost: DEFAULT_PRICE,
  countryOfOrigin: null,
  materialLocationCountry: DEFAULT_MATERIAL_LOCATION_COUNTRY_INTERNATIONAL,
  dealQuantity: null,
  dealQuantityUnit: DEFAULT_PRIMARY_UNIT,
  productPackaging: null,
  profitMarginExpected: null,
  moq: null,
  moqUnit: DEFAULT_PRIMARY_UNIT
};

export const CONSOLIDATION_ENQUIRY_QUOTE_DEFAULT_VALUE = {
  quoteIsExpanded: true,
  product: [CONSOLIDATION_PRODUCT_DEFAULT_VALUE],
  totalWeight: 0,
  totalWeightUnit: DEFAULT_PRIMARY_UNIT,
  totalLocalFreight: DEFAULT_PRICE,
  totalPortAndClearanceExpenses: DEFAULT_PRICE,
  totalFreight: DEFAULT_PRICE,
  switchBlCost: DEFAULT_PRICE,
  portOfLoading: null,
  portOfDischarge: null,
  insuranceCost: DEFAULT_INSURANCE_COST,
  financeCost: null,
  discount: null,
  incoTerms: DEFAULT_INCO_TERMS,
  incoTermsLocation: null
};

export const DOMESTIC_NON_CONSOLIDATION_ENQUIRY_QUOTE_DEFAULT_VALUE = {
  quote_id: null,
  isExpanded: true,
  productName: null,
  productPackaging: null,
  dealQuantity: null,
  dealQuantityUnit: DEFAULT_PRIMARY_UNIT,
  moq: null,
  moqUnit: DEFAULT_PRIMARY_UNIT,
  totalWeight: 0,
  totalWeightUnit: DEFAULT_PRIMARY_UNIT,
  totalLocalFreight: DEFAULT_PRICE,
  warehousingCost: DEFAULT_PRICE,
  base_price: DEFAULT_RATE_PRICE,
  additionalCost: DEFAULT_PRICE,
  finalQuote_selling_price: DEFAULT_RATE_PRICE,
  countryOfOrigin: null,
  materialLocationCountry: DEFAULT_MATERIAL_LOCATION_COUNTRY_DOMESTIC,
  insuranceCost: DEFAULT_INSURANCE_COST,
  discount: null,
  profitMarginExpected: null,
  financeCost: null,
  incoTermsZipCode: null,
  incoTermsLocation: null
};

// Customer enquiry values that are spelled differently in this app
const CUSTOMER_ENQUIRY_CURRENCY_MAP: Record<string, string> = { EUR: 'EURO' };
const CUSTOMER_ENQUIRY_UNIT_MAP: Record<string, string> = { LB: 'LBS' };
const INCO_TERMS_WITH_LOCATION = ['EXW', 'DDP', 'DAP'];

const findCurrencyOption = (currency?: string | null) => {
  if (!currency) return undefined;
  const value = currency.trim().toUpperCase();
  return CURRENCY.find((item) => item.value === (CUSTOMER_ENQUIRY_CURRENCY_MAP[value] ?? value));
};

const findWeightUnitOption = (unit?: string | null) => {
  if (!unit) return undefined;
  const value = unit.trim().toUpperCase();
  const unitValue = CUSTOMER_ENQUIRY_UNIT_MAP[value] ?? value;
  for (const weightSystem of WEIGHT_SYSTEM) {
    const weightUnit = weightSystem.childOptions?.find((item) => item.value === unitValue);
    if (weightUnit) return { weightSystem, weightUnit };
  }
  return undefined;
};

const parseQuantity = (quantity?: string | null) => {
  if (quantity === null || quantity === undefined) return null;
  const value = parseFloat(quantity);
  return isNaN(value) ? null : value;
};

/**
 * Default quote block with every price currency and weight unit switched to the
 * enquiry's primary currency / unit, so the cost calculator starts consistent.
 */
const getQuoteDefaultValue = (currency: ICurrency, primaryUnit: IWeightChildOption) => {
  const quote: any = {
    ...NON_CONSOLIDATION_ENQUIRY_QUOTE_DEFAULT_VALUE,
    dealQuantityUnit: primaryUnit,
    moqUnit: primaryUnit,
    totalWeightUnit: primaryUnit
  };
  Object.keys(quote).forEach((key) => {
    const value = quote[key];
    if (value && typeof value === 'object' && 'currency' in value) {
      quote[key] = {
        ...value,
        currency,
        ...('weightUnit' in value ? { weightUnit: primaryUnit } : {})
      };
    }
  });
  return quote as INonConsolidateQuote;
};

const mapCustomerEnquiryQuote = (
  item: ICustomerEnquiryQuote,
  currency: ICurrency,
  weightSystem: IWeightSystemOption,
  primaryUnit: IWeightChildOption
): INonConsolidateQuote => {
  const quote: any = getQuoteDefaultValue(currency, primaryUnit);

  const productName = item.product_details?.name;
  if (productName) {
    // Free-text enquiry products have no product_id; the product field accepts new names
    quote.productName = toQuoteProductOption({
      product_name: productName,
      product_id: item.product_details?.product_id ?? null,
      cpd_product_id: item.product_details?.cpd_product_id ?? null
    });
  }

  const dealQuantity = parseQuantity(item.quantity);
  if (dealQuantity !== null) quote.dealQuantity = dealQuantity;

  if (item.quantity_unit) {
    // Units outside the primary weight system aren't selectable, so leave them empty
    quote.dealQuantityUnit =
      weightSystem.childOptions.find(
        (option) => option.value === findWeightUnitOption(item.quantity_unit)?.weightUnit.value
      ) ?? null;
  }

  if (item.packaging) quote.productPackaging = item.packaging;

  if (item.deal_incoterm) {
    const incoTermValue = item.deal_incoterm.trim().toUpperCase();
    quote.incoTerms = ENQUIRY_INCO_TERMS.find((term) => term.value === incoTermValue) ?? null;
  }

  // Other inco terms use port dropdowns, which free-text locations can't fill
  if (item.deal_incoterm_location && INCO_TERMS_WITH_LOCATION.includes(quote.incoTerms?.value)) {
    quote.incoTermsLocation = item.deal_incoterm_location;
  }

  return quote;
};

export const mapCustomerEnquiryToQuotePrefill = (
  customerEnquiryId: string,
  data: ICustomerEnquiryPrefill
): IQuotePrefill => {
  const quotes = data?.quotes?.length ? data.quotes : [];
  const firstQuote = quotes[0];

  const currency1 =
    findCurrencyOption(data?.currency_type_one) ??
    findCurrencyOption(firstQuote?.target_price_currency) ??
    DEFAULT_CURRENCY;
  const unitMatch = findWeightUnitOption(firstQuote?.quantity_unit);
  const weightSystem = unitMatch?.weightSystem ?? DEFAULT_WEIGHT_SYSTEM;
  const primaryUnit = unitMatch?.weightUnit ?? DEFAULT_PRIMARY_UNIT;

  return {
    customerEnquiryId,
    quoteForm: {
      genericInformation: {
        ...GENERIC_INFORMATION_DEFAULT_VALUE,
        currency1,
        weightSystem,
        primaryUnit
      } as any,
      quote: quotes.length
        ? quotes.map((item) => mapCustomerEnquiryQuote(item, currency1, weightSystem, primaryUnit))
        : [getQuoteDefaultValue(currency1, primaryUnit)]
    },
    enquiryForm: {
      // The customer is submitted as hubspot_customer_id, so a name alone can't select one
      customer:
        data?.hubspot_customer_id && data?.customer_name
          ? { id: data.hubspot_customer_id, customer_name: data.customer_name }
          : null,
      remarks: data?.remarks ?? ''
    }
  };
};

/**
 * Country of Origin (`origin`): new quotes store an ISO-2 code; old quotes hold free text.
 * The backend returns `origin_display_value` (country name for codes, free text otherwise).
 * The option keeps the raw stored value as `value`, so an untouched legacy free-text origin
 * is sent back unchanged, while a picked country sends its code.
 */
export const getCountryOfOriginOption = (origin?: string | null, displayValue?: string | null) =>
  origin
    ? { value: origin, label: displayValue || getCountryOptionByCode(origin)?.label || origin }
    : null;

export const getCountryOfOriginPayload = (countryOfOrigin: any): string | null => {
  if (!countryOfOrigin) return null;
  if (typeof countryOfOrigin === 'string') return countryOfOrigin;
  return countryOfOrigin.value ?? null;
};

const humanizeErrorKey = (key: string) => {
  const text = key.replace(/_/g, ' ').trim();
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const flattenApiError = (error: any, key?: string): string[] => {
  if (error === null || error === undefined || error === '') return [];
  if (typeof error === 'string' || typeof error === 'number') {
    return [key && !/^\d+$/.test(key) ? `${humanizeErrorKey(key)}: ${error}` : String(error)];
  }
  if (Array.isArray(error)) {
    const isPlainList = error.every((item) => typeof item === 'string');
    if (isPlainList && key && !/^\d+$/.test(key)) {
      return [`${humanizeErrorKey(key)}: ${error.join(', ')}`];
    }
    return error.flatMap((item) => flattenApiError(item, key));
  }
  if (typeof error === 'object') {
    return Object.entries(error).flatMap(([childKey, value]) => flattenApiError(value, childKey));
  }
  return [];
};

/**
 * Quote APIs return `error` either as a string or as a (possibly nested) field-error object,
 * e.g. { material_location_country: ['"XX" is not a valid choice.'] }. Always returns a string
 * so it is safe to render in a toast.
 */
export const getApiErrorMessage = (response: any, fallback = 'Something Went Wrong') => {
  for (const source of [response?.error, response?.message]) {
    const messages = flattenApiError(source);
    if (messages.length) return Array.from(new Set(messages)).join('; ');
  }
  return fallback;
};

/**
 * Product field value used by every quote form:
 * { product_id, product_name, cpd_product_id, is_new_cpd_selection? }
 * - product_id: local UUID (always present on saved products)
 * - cpd_product_id: CPD master_id, null on older products
 * - is_new_cpd_selection: set only when the user picks a product from the CPD dropdown
 */
export interface IQuoteProductOption {
  product_id: string | null;
  product_name: string;
  cpd_product_id: string | null;
  is_new_cpd_selection?: boolean;
}

/**
 * Normalises any backend product shape to IQuoteProductOption:
 * - listing `product_data` / detail `product_details`: { product_id, product_name, cpd_product_id }
 * - consolidation (MTT) `product_data`: { id, name, cpd_product_id }
 */
export const toQuoteProductOption = (source: any): IQuoteProductOption | null => {
  if (!source) return null;
  const product_name = source.product_name ?? source.name ?? null;
  const product_id = source.product_id ?? source.id ?? null;
  if (!product_name && !product_id) return null;
  return { product_id, product_name, cpd_product_id: source.cpd_product_id ?? null };
};

/**
 * Request `product_details`. Backend resolves id → cpd_product_id → name.
 * - newly picked from CPD → { cpd_product_id, name } (old id must NOT be sent)
 * - unchanged saved product (edit / duplicate) → { id, name }
 * - legacy free-text name (e.g. from a customer enquiry) → { name }
 */
export const getProductDetailsPayload = (product: any) => {
  if (!product) return null;
  const name = product.product_name ?? product.name ?? null;
  if (product.is_new_cpd_selection && product.cpd_product_id) {
    return { cpd_product_id: product.cpd_product_id, name };
  }
  const id = product.product_id ?? product.id ?? null;
  if (id) return { id, name };
  if (product.cpd_product_id) return { cpd_product_id: product.cpd_product_id, name };
  return { name };
};
